import React, { useLayoutEffect, useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import { MenuList } from '@atlaskit/navigation-system/side-nav-items/menu-list';

import type { GlobalSideNavContentProps, NavItemConfig } from '../../common/types';
import NavItem from '../../common/ui/nav-item';
import { buildCustomizationDataShapes } from '../../common/utils/build-customization-data-shapes';
import { useNavAnalytics } from '../../common/utils/use-nav-analytics';
import { useOptions } from '../../controllers/options-context';
import { SideNavDataProvider } from '../../controllers/side-nav-data-provider';
import { useUserMenuData } from '../../controllers/user-state/user-menu-data-provider';
import { GLOBAL_SIDE_NAV_TEST_ID } from '../constants';
import makeForYouNavItem from '../for-you';
import { SideNavigationLayout } from '../layout';
import { messages } from '../messages';
import { PostMainItems } from '../post-main-items';
import makeRecentNavItem from '../recent';
import makeStarredNavItem from '../starred';

import { useModifyNavItemForAnalytics } from './use-modify-nav-item-for-analytics';

export function GlobalSideNavContent(props: GlobalSideNavContentProps) {
	const {
		bottomElem,
		items,
		forYou,
		postItemsElem,
		postMainElem,
		shortcuts,
		growthItemShortcuts,
		topElem,
	} = props;

	const { formatMessage } = useIntl();

	const { userMenuData, setUserMenuData } = useUserMenuData();
	const { disableItemCustomization } = useOptions();
	const { fireViewSideNavEvent, fireClickSideNavNavItemEvent } = useNavAnalytics();
	const { modifyNavItemForAnalytics } = useModifyNavItemForAnalytics();

	// Prepare global items
	const globalItems = React.useMemo(() => {
		const items = new Map<string, NavItemConfig>();
		if (!forYou.hideForYou) {
			items.set(
				forYou.forYouMenuItemId ?? 'global.sidebar.forYou',
				makeForYouNavItem({
					href: forYou.forYouUrl,
					isSelected: forYou.forYouIsSelected,
					onClick: forYou.forYouOnClick,
				}),
			);
		}
		if (!forYou.hideRecents) {
			items.set(
				forYou.recentsMenuItemId ?? 'global.sidebar.recent',
				makeRecentNavItem({
					useRecentItems: forYou.useRecentItems,
					searchPlaceholder: formatMessage(messages.recentsSearchPlaceholder),
					searchable: forYou.recentsIsSearchable,
					filters: forYou.recentsFilters,
					viewAllHref: forYou.viewAllRecentsUrl,
					viewAllOnClick: forYou.viewAllRecentsOnClick,
				}),
			);
		}
		if (!forYou.hideStarred) {
			items.set(
				forYou.starredMenuItemId ?? 'global.sidebar.starred',
				makeStarredNavItem({
					useStarredItems: forYou.useStarredItems,
					searchPlaceholder: formatMessage(messages.starredSearchPlaceholder),
					searchable: forYou.starredIsSearchable,
					filters: forYou.starredFilters,
					viewAllHref: forYou.viewAllStarredUrl,
					viewAllOnClick: forYou.viewAllStarredOnClick,
				}),
			);
		}
		return items;
	}, [forYou, formatMessage]);

	const allNavItems = React.useMemo(
		() => ({ ...Object.fromEntries(globalItems), ...items }),
		[globalItems, items],
	);

	// todo: Move this to the outer context for customization data. We only need one.
	const sideNavMenuData = useMemo(() => {
		const navData = buildCustomizationDataShapes(
			Object.keys(allNavItems),
			shortcuts ? Object.keys(shortcuts) : [],
			// All menu items are visible when there is no customization data
			disableItemCustomization ? [] : userMenuData,
		);
		return { ...navData, setUserMenuData };
	}, [allNavItems, disableItemCustomization, setUserMenuData, shortcuts, userMenuData]);

	// Used for analytics, add +1 for more menu/customise button & bottomElem to globalItemsCount
	const additionalGlobalItemsCount = (disableItemCustomization ? 0 : 1) + (bottomElem ? 1 : 0);

	const growthItemsCount = React.useMemo(
		() => Object.keys(growthItemShortcuts ?? {}).length,
		[growthItemShortcuts],
	);

	const totalNavItemsCount = React.useMemo(
		() => sideNavMenuData.visibleMenuIds.length + additionalGlobalItemsCount + growthItemsCount,
		[additionalGlobalItemsCount, growthItemsCount, sideNavMenuData.visibleMenuIds.length],
	);

	const renderNavItem = React.useCallback(
		(id: string, index: number) => {
			const handleClickSideNavItem = () => {
				fireClickSideNavNavItemEvent(
					id,
					index,
					items,
					globalItems,
					sideNavMenuData,
					totalNavItemsCount,
					additionalGlobalItemsCount,
				);
			};

			const item = modifyNavItemForAnalytics(
				id,
				allNavItems[id],
				globalItems,
				items,
				sideNavMenuData.visibleItemMenuIds,
			);

			return <NavItem key={id} item={item} onClick={handleClickSideNavItem} />;
		},
		[
			additionalGlobalItemsCount,
			allNavItems,
			fireClickSideNavNavItemEvent,
			globalItems,
			items,
			modifyNavItemForAnalytics,
			sideNavMenuData,
			totalNavItemsCount,
		],
	);

	useLayoutEffect(() => {
		fireViewSideNavEvent(
			forYou,
			sideNavMenuData,
			Object.keys(items),
			!disableItemCustomization,
			!!bottomElem,
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<SideNavDataProvider value={sideNavMenuData}>
			<SideNavigationLayout
				testId={props.testId ?? GLOBAL_SIDE_NAV_TEST_ID}
				preMainElem={topElem}
				postMainElem={
					<PostMainItems
						postItemsElem={postItemsElem}
						postMainElem={postMainElem}
						globalItems={globalItems}
						shortcuts={shortcuts}
						allNavItems={allNavItems}
						growthItemsCount={growthItemsCount}
						growthItemShortcuts={growthItemShortcuts}
						totalNavItemsCount={totalNavItemsCount}
						additionalGlobalItemsCount={additionalGlobalItemsCount}
					/>
				}
				bottomElem={bottomElem}
			>
				<MenuList>{sideNavMenuData.visibleItemMenuIds.map(renderNavItem)}</MenuList>
			</SideNavigationLayout>
		</SideNavDataProvider>
	);
}
